/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// tslint:disable: max-func-body-length

import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore
} from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./checkout';
import { updateCustomerAttributes, updateCustomerAttributesInput } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent, Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import { AttributeDefinition, CartLine, Customer } from '@msdyn365-commerce/retail-proxy';
import get from 'lodash/get';
import * as React from 'react';
import { ICheckoutProps } from '../definition-extensions/checkout.ext.props.autogenerated';
import { ICheckoutData } from './checkout.data';
import GuidedFormComponent, { ICheckoutGuidedFormProps } from './components/checkout/guided-form';

export interface IOrderSummaryCartLineItems {
    cartLines: CartLine[] | undefined;
    formattedPrice: string | undefined;
    clubPlusText: string;
    clubPlusTrialText: string;
}

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore }) => {
    return (
        <Node {...LineItem}>
            {item}
            {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        </Node>
    );
};

export interface ICheckoutPlaceOrderButtonProps {
    canPlaceOrder: boolean;
    checkoutBtnText: string;
    telemetryContent?: ITelemetryContent;
    placeOrder(): Promise<void>;
}

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({ LineItems, Header, heading, editLink, itemsForPickup, itemsForShip }) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
    </Node>
);

const OrderSummaryComponent: React.FC<IOrderSummary & IOrderSummaryCartLineItems> = props => {
    const { heading, lines, formattedPrice, clubPlusText, clubPlusTrialText } = props;

    // const cartLine = cartLines && cartLines[0];
    return (
        <div className='msc-order-summary-wrapper'>
            {heading}
            <div className='msc-order-summary__items'>
                {lines && (
                    <>
                        <div>
                            {/* Display Club Plus Trial Text if Total Price is 0 */}
                            <span>{formattedPrice === '£0.00' || formattedPrice === '£0.01' ? clubPlusTrialText : clubPlusText}</span>
                            <span>{formattedPrice}</span>
                        </div>
                        {lines.subtotal}
                        {lines.shipping}
                        {lines.otherCharge}
                        {lines.loyalty}
                        {lines.giftCard}
                        {lines.orderTotal}
                    </>
                )}
            </div>
        </div>
    );
};

/* VSI Customization - START - 18/11/20 */
const getSlotItems = (key: string, props: ICheckoutViewProps): React.ReactNode[] => {
    const { slots } = props;
    return slots && slots[key] && slots[key].length ? slots[key] : [];
};

const getId = (item: React.ReactNode): string => {
    return get(item, 'props.id') || '';
};
/* VSI Customization - END */
// tslint:disable-next-line: max-func-body-length

const CheckoutView: React.FC<ICheckoutViewProps & ICheckoutProps<ICheckoutData> & ICheckoutGuidedFormProps> = props => {
    const {
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        bodyProps,
        mainProps,
        sideProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        orderSummary,
        placeOrderButton,
        termsAndConditions,
        resources,
        mainControlProps,
        resources: { clubPlusText, clubPlusTrialText },
        config: { breadCrumbsRoot, breadCrumbs }
    } = props;

    const [attributes, setAttributes] = React.useState<AttributeDefinition[] | undefined>();
    const [customer, setCustomer] = React.useState<Customer | undefined>();

    const [isDDRP, setIsDDRP] = React.useState(true);
    const cartLines: CartLine[] | undefined = props.data.checkout?.result?.checkoutCart.cart.CartLines;
    const price = cartLines && cartLines[0] && cartLines[0].Price;

    const handleUpdateAttributes = async () => {
        const newAttributes = customer!.Attributes!.map(attr => {
            let newAttrValue = { ...attr.AttributeValue };
            if (attr.Name === 'Direct Debit') {
                newAttrValue = { ...attr.AttributeValue, StringValue: isDDRP ? 'DDRP' : 'None' };
            }

            return { ...attr, AttributeValue: newAttrValue };
        });
        const input = new updateCustomerAttributesInput(
            customer!.AccountNumber,
            newAttributes || [],
            props.context.request.apiSettings,
            customer!.Phone,
            customer!.VatNumber
        );
        await updateCustomerAttributes(input, props.context.actionContext);
    };

    let formattedPrice: number | string | undefined;
    /* VSI Customization - START - 24/08/21 */

    if (price === 0) {
        formattedPrice = '£0.00';
    } else {
        formattedPrice = price && props.context.cultureFormatter.formatCurrency(price, props.context.request.channel?.Currency);
    }

    /* VSI Customization - END - 24/08/21 */

    /* VSI Customization - START - 18/11/20 */
    const items = getSlotItems('checkoutInformation', props);
    let creditBannerFound = false;
    let creditBanner: React.ReactNode = <></>;
    const checkoutItems = items;
    // store text block in credit-banner

    React.useEffect(() => {
        setAttributes(props.data.attributeDefinitions.result!);
        setCustomer(props.data.customerInformation.result!);
    }, []);

    items &&
        items.map(item => {
            if (creditBannerFound === false) {
                const childId = getId(item);
                if (childId.includes('text-block')) {
                    creditBanner = item;
                    creditBannerFound = true;
                }
            }
        });
    const creditBannerSection = creditBanner && React.cloneElement(creditBanner as React.ReactElement);
    // @ts-ignore
    const termsAndPlaceOrder: React.ReactElement = (
        <>
            <div role='button' onClick={handleUpdateAttributes}>
                <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                <Node {...mainControlProps}>{placeOrderButton}</Node>
            </div>
        </>
    );
    /* VSI Customization - END - 30/11/20 */
    if (!props.context.request.user.isAuthenticated) {
        const signinURL = props.context.request.user.signInUrl;
        window.location.assign(signinURL!);
    }
    if (!props.context.request.user.isAuthenticated) {
        return (
            <div className='loading-message-container'>
                <div className='loading-message-container__innerbox'>
                    <Waiting />
                    <span>{props.resources.authenticationMessage}</span>
                </div>
            </div>
        );
    } else {
        return (
            <Module {...checkoutProps}>
                {!hasSalesOrder && <Node {...headerProps}>{title}</Node>}
                {!hasSalesOrder && (
                    <Node {...bodyProps}>
                        {loading}
                        {alert}
                        {canShow && (
                            <>
                                <Node {...mainProps}>
                                    {/* VSI Customization - START - 18/11/20 */}
                                    {
                                        <GuidedFormComponent
                                            {...{
                                                items: checkoutItems,
                                                moduleState: props.moduleState,
                                                disableGuidedCheckoutFlow: props.disableGuidedCheckoutFlow,
                                                resource: resources,
                                                isMobile: props.isMobile,
                                                isEditor: false,
                                                rootLink: breadCrumbsRoot,
                                                breadCrumbsText: breadCrumbs,
                                                placeOrderBtn: <></>, // Removing place order button for single payment
                                                attributes: attributes,
                                                setIsDDRP: setIsDDRP,
                                                isDDRP: isDDRP,
                                                customer: customer!,
                                                context: props.context
                                            }}
                                        />
                                    }
                                    {/* VSI Customization - END */}
                                </Node>
                                <Node {...sideProps}>
                                    {orderSummary && (
                                        <OrderSummaryComponent
                                            formattedPrice={formattedPrice?.toString()}
                                            cartLines={cartLines}
                                            {...orderSummary}
                                            clubPlusText={clubPlusText}
                                            clubPlusTrialText={clubPlusTrialText}
                                        />
                                    )}

                                    {creditBannerSection && (
                                        <Node className='credit-availability-banner' tag='div'>
                                            {creditBannerSection}
                                        </Node>
                                    )}
                                    {/* VSI-Customization -- START Don't need LineItems ATM  */}
                                    {/* {lineItems && <LineItemsComponent {...lineItems} />} */}
                                    {/* VSI-Customization -- END  */}
                                </Node>
                            </>
                        )}
                    </Node>
                )}
                {placeOrderButton}
                {hasSalesOrder && orderConfirmation}
            </Module>
        );
    }
};

export default CheckoutView;
